import React, { useEffect, useState } from "react";
import {
    Button,
    Space,
    List,
    Typography,
    Skeleton,
    Tag,
    notification,
} from "antd";
import AppShell, { AutoSyncBadge } from "Components/Layout/AppShell";
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    PlusOutlined,
    MinusOutlined,
    LineChartOutlined,
} from "@ant-design/icons";
import PortfolioOnBuy from "Components/Forms/PortfolioOnBuy";
import PortfolioOnSell from "Components/Forms/PortfolioOnSell";
import { useDispatch, useSelector } from "react-redux";
import * as assetAction from "store/Actions/asset";
import * as portfolioAction from "store/Actions/portfolio";
import TransactionListView from "Components/ListView/Transaction";
import ViewPortfolio from "Components/ListView/ViewPortfolio";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { getCoinInfo } from "utils/CoinGecko";
import {
    mpTapBuyCTA,
    mpTapGoBack,
    mpTapSellCTA,
    mpViewMarketData,
} from "Shared/Mixpanel/AssetsManager";
import Transaction from "./Transaction";
import MarketView from "./MarketView";

const { Title, Text } = Typography;

const isBuyTransaction = (tx) => {
    const typeLower = String(tx?.type || "").toLowerCase();
    if (typeLower === "buy") return true;
    if (typeLower === "sell") return false;
    const snap = tx?.portfolioSnapshot || tx || {};
    const prevCoins = Number(
        (snap.previous || tx?.previous || {}).numberOfCoins || 0
    );
    const nextCoins = Number(
        (snap.latest || tx?.latest || {}).numberOfCoins || 0
    );
    return nextCoins >= prevCoins;
};

const AssetManager = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { search } = useLocation();
    const { name: routePortfolioName, id: routeAssetId } = useParams() || {};
    const intent = new URLSearchParams(search).get("intent");
    const portfolios = useSelector(
        (state) => state.portfolios?.portfolios || []
    );
    const selectedPortfolio = useSelector(
        (state) => state.portfolios?.selectedPortfolio || {}
    );
    const settings = useSelector((state) => state.settings || {});
    const displayCurrency = settings?.currency || "INR";
    const currSymbol = getCurrencySymbol(displayCurrency);

    document.title = `Viewing ${selectedPortfolio.name || ""} — Portfolio`;
    const asset = useSelector((state) => state.assets?.selectedAsset || {});
    const assets = useSelector((state) => state.assets?.assets || []);
    const [showBuyDrawer, setShowBuyDrawer] = useState(intent === "buy");
    const [showSellDrawer, setShowSellDrawer] = useState(intent === "sell");
    const [showMarket, setShowMarket] = useState(intent === "market");
    const [coinGeckoInfo, setCoinGeckoInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sideFilter, setSideFilter] = useState("ALL");
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        let activePortfolio = selectedPortfolio;
        if (routePortfolioName && Array.isArray(portfolios)) {
            const decodedPortfolio = decodeURIComponent(routePortfolioName);
            if (
                !selectedPortfolio?.uid ||
                (selectedPortfolio.name !== decodedPortfolio &&
                    selectedPortfolio.uid !== decodedPortfolio)
            ) {
                const matchedPortfolio = portfolios.find(
                    (p) =>
                        p.name === decodedPortfolio ||
                        p.uid === decodedPortfolio
                );
                if (matchedPortfolio) {
                    activePortfolio = matchedPortfolio;
                    dispatch(
                        portfolioAction.setSelectedPortfolio(matchedPortfolio)
                    );
                }
            }
        }
        if (routeAssetId && Array.isArray(assets)) {
            const decodedAssetId = decodeURIComponent(routeAssetId);
            if (
                !asset?.uid ||
                (asset.id !== decodedAssetId && asset.uid !== decodedAssetId)
            ) {
                const matchedAsset = assets.find(
                    (a) =>
                        (a.id === decodedAssetId || a.uid === decodedAssetId) &&
                        (!activePortfolio?.uid ||
                            a.portfolioId === activePortfolio.uid)
                );
                if (matchedAsset) {
                    dispatch(assetAction.setSelectedAsset(matchedAsset));
                }
            }
        }
    }, [
        routePortfolioName,
        routeAssetId,
        portfolios,
        assets,
        selectedPortfolio,
        asset,
        dispatch,
    ]);

    const getCoin = async () => {
        if (!asset || isEmpty(asset)) {
            setLoading(false);
            return;
        }
        setLoading(true);
        const response = await getCoinInfo([asset], displayCurrency);
        setCoinGeckoInfo(response && response[0] ? response[0] : null);
        if (!response || response.length === 0) {
            notification.error({
                message: `Error fetching ${asset.name}`,
                description: `Market data is temporarily unavailable. Please try again shortly.`,
            });
        }
        setLoading(false);
    };

    useEffect(() => {
        if (asset && !isEmpty(asset)) {
            getCoin();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [displayCurrency, asset?.uid, asset?.id]);

    const onClose = () => {
        mpTapGoBack({
            screen: "Asset Manager",
        });
        history.push(`/portfolios/${selectedPortfolio.name}`);
    };

    const deleteAsset = () => {
        const remaining = (assets || []).filter(
            (item) => item.uid !== asset.uid
        );
        dispatch(assetAction.updateBackedupAssets(remaining));
        dispatch(assetAction.setPortfolioAssets(selectedPortfolio));
        history.push(`/portfolios/${selectedPortfolio.name}`);
    };

    const txList = asset?.transactions || [];
    let totalBought = 0;
    let totalBoughtCoins = 0;
    let totalSold = 0;
    let totalSoldCoins = 0;
    let buyCount = 0;
    let sellCount = 0;

    txList.forEach((tx) => {
        const snap = tx?.portfolioSnapshot || tx || {};
        const prev = snap.previous || tx?.previous || {};
        const next = snap.latest || tx?.latest || {};
        const prevCoins = Number(prev.numberOfCoins || 0);
        const nextCoins = Number(next.numberOfCoins || 0);
        const coinDelta = Math.abs(nextCoins - prevCoins);
        const price = Number(
            snap.transactionMarketPrice ?? tx?.transactionMarketPrice ?? 0
        );
        const invDelta = Math.abs(
            Number(next.investment || 0) - Number(prev.investment || 0)
        );
        const txValue = coinDelta * price > 0 ? coinDelta * price : invDelta;

        if (isBuyTransaction(tx)) {
            totalBought += txValue;
            totalBoughtCoins += coinDelta;
            buyCount += 1;
        } else {
            totalSold += txValue;
            totalSoldCoins += coinDelta;
            sellCount += 1;
        }
    });

    const txCount = txList.length;
    const totalVolume = totalBought + totalSold;
    const netFlow = totalBought - totalSold;
    const numberOfCoins = Number(asset?.portfolio?.latest?.numberOfCoins || 0);
    const isExited = numberOfCoins <= 1e-10 && txCount > 0;
    const realizedPL =
        asset?.realizedPL !== undefined
            ? Number(asset.realizedPL)
            : totalSold - totalBought;
    const avgExitPrice = totalSoldCoins > 0 ? totalSold / totalSoldCoins : 0;

    const tradeStats = {
        totalVolume,
        totalBought,
        totalBoughtCoins,
        totalSold,
        totalSoldCoins,
        netFlow,
        realizedPL,
        isExited,
        avgExitPrice,
    };

    const reversedTransactions = [...txList].reverse();
    const filteredTransactions = reversedTransactions.filter((tx) => {
        if (sideFilter === "BUY") return isBuyTransaction(tx);
        if (sideFilter === "SELL") return !isBuyTransaction(tx);
        return true;
    });

    const profitColor = Tokens.profit || Tokens.positive;
    const lossColor = Tokens.loss || Tokens.negative;
    const marketInfo = coinGeckoInfo || {
        current_price: Number(asset?.portfolio?.latest?.marketPrice) || 0,
        symbol: asset?.symbol || "",
        name: asset?.name || "",
        image: asset?.avatar || "",
    };
    const coinSymbolUpper = (
        asset?.symbol ||
        marketInfo?.symbol ||
        ""
    ).toUpperCase();

    return (
        <div>
            {!loading && asset && !isEmpty(asset) ? (
                <div>
                    <AppShell
                        activeTab="overview"
                        title={
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "12px",
                                    maxWidth: Tokens.containerMaxWidth,
                                    margin: "0 auto",
                                    width: "100%",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        minWidth: 0,
                                    }}
                                >
                                    <Button
                                        type="text"
                                        icon={
                                            <ArrowLeftOutlined
                                                style={{
                                                    color: Tokens.textSecondary,
                                                    fontSize: "16px",
                                                }}
                                            />
                                        }
                                        onClick={onClose}
                                        title="Back to portfolio"
                                        style={{
                                            padding: 0,
                                            width: "32px",
                                            height: "32px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    />
                                    {(marketInfo.image || asset.avatar) && (
                                        <img
                                            src={
                                                marketInfo.image || asset.avatar
                                            }
                                            alt={asset.name}
                                            style={{
                                                width: "28px",
                                                height: "28px",
                                                borderRadius: "50%",
                                                objectFit: "cover",
                                                flexShrink: 0,
                                            }}
                                        />
                                    )}
                                    <Title
                                        level={4}
                                        style={{
                                            color: Tokens.textPrimary,
                                            margin: 0,
                                            fontWeight: 500,
                                            fontSize: "18px",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {asset.name}
                                    </Title>
                                    {coinSymbolUpper && (
                                        <Tag
                                            style={{
                                                margin: 0,
                                                backgroundColor: Tokens.raised,
                                                color: Tokens.textSecondary,
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius: Tokens.radiusTag,
                                                fontFamily: Tokens.fontMono,
                                                fontSize: "11px",
                                                padding: "1px 6px",
                                                flexShrink: 0,
                                            }}
                                        >
                                            {coinSymbolUpper}
                                        </Tag>
                                    )}
                                    <AutoSyncBadge />
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Text
                                        style={{
                                            color: Tokens.textPrimary,
                                            fontFamily: Tokens.fontMono,
                                            fontVariantNumeric: "tabular-nums",
                                            fontWeight: 500,
                                            fontSize: "14px",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {currSymbol}
                                        {getFormattedAmount(
                                            marketInfo.current_price,
                                            true,
                                            2
                                        )}
                                    </Text>
                                    <Button
                                        type="text"
                                        data-testid="market-stats-btn"
                                        icon={
                                            <LineChartOutlined
                                                style={{
                                                    color: Tokens.textSecondary,
                                                    fontSize: "15px",
                                                }}
                                            />
                                        }
                                        title="Market stats"
                                        onClick={() => {
                                            mpViewMarketData({
                                                coin: asset.name,
                                            });
                                            setShowMarket(true);
                                        }}
                                        style={{
                                            backgroundColor: Tokens.raised,
                                            border: `1px solid ${Tokens.hairline}`,
                                            borderRadius: Tokens.radiusControl,
                                            width: "34px",
                                            height: "34px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    />
                                    {confirmDelete ? (
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px",
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                danger
                                                type="primary"
                                                data-testid="confirm-remove-coin-btn"
                                                onClick={deleteAsset}
                                                style={{
                                                    height: "34px",
                                                    borderRadius:
                                                        Tokens.radiusControl,
                                                    fontWeight: 500,
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Remove
                                            </Button>
                                            <Button
                                                size="small"
                                                data-testid="cancel-remove-coin-btn"
                                                onClick={() =>
                                                    setConfirmDelete(false)
                                                }
                                                style={{
                                                    height: "34px",
                                                    borderRadius:
                                                        Tokens.radiusControl,
                                                    backgroundColor:
                                                        Tokens.raised,
                                                    borderColor:
                                                        Tokens.hairline,
                                                    color: Tokens.textSecondary,
                                                    fontSize: "12px",
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            type="text"
                                            danger
                                            data-testid="remove-coin-btn"
                                            icon={<DeleteOutlined />}
                                            title="Remove coin"
                                            onClick={() =>
                                                setConfirmDelete(true)
                                            }
                                            style={{
                                                backgroundColor: Tokens.raised,
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius:
                                                    Tokens.radiusControl,
                                                width: "34px",
                                                height: "34px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        }
                        placement="right"
                        closable={false}
                        push={false}
                        height="100%"
                        width="100%"
                        open
                        headerStyle={{
                            backgroundColor: Tokens.bg,
                            borderBottom: `1px solid ${Tokens.hairline}`,
                            padding: "16px 24px",
                        }}
                        bodyStyle={{
                            backgroundColor: Tokens.bg,
                            padding: "24px 16px 80px",
                        }}
                        footerStyle={{
                            backgroundColor: Tokens.card,
                            borderTop: `1px solid ${Tokens.hairline}`,
                            padding:
                                "12px 16px calc(12px + env(safe-area-inset-bottom, 0px))",
                        }}
                        footer={
                            <div
                                style={{
                                    maxWidth: Tokens.containerMaxWidth,
                                    margin: "0 auto",
                                    width: "100%",
                                    display: "flex",
                                    gap: "10px",
                                    justifyContent: "space-between",
                                }}
                            >
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    style={{
                                        flex: 1,
                                        height: "40px",
                                        borderRadius: Tokens.radiusControl,
                                        fontWeight: 500,
                                    }}
                                    onClick={() => {
                                        mpTapBuyCTA({
                                            type: "primary",
                                        });
                                        setShowBuyDrawer(true);
                                    }}
                                >
                                    Buy
                                </Button>
                                <Button
                                    icon={<MinusOutlined />}
                                    disabled={numberOfCoins <= 0}
                                    style={{
                                        flex: 1,
                                        height: "40px",
                                        borderRadius: Tokens.radiusControl,
                                        fontWeight: 500,
                                        backgroundColor:
                                            numberOfCoins > 0
                                                ? Tokens.raised
                                                : "transparent",
                                        borderColor: Tokens.hairline,
                                        color:
                                            numberOfCoins > 0
                                                ? Tokens.textPrimary
                                                : Tokens.textMuted,
                                    }}
                                    onClick={() => {
                                        mpTapSellCTA();
                                        setShowSellDrawer(true);
                                    }}
                                >
                                    Sell
                                </Button>
                            </div>
                        }
                    >
                        <div
                            style={{
                                maxWidth: Tokens.containerMaxWidth,
                                margin: "0 auto",
                                width: "100%",
                            }}
                        >
                            {(!isEmpty(asset.portfolio) || txCount > 0) && (
                                <ViewPortfolio
                                    coinInfo={marketInfo}
                                    coinGeckoInfo={marketInfo}
                                    tradeStats={tradeStats}
                                    currency={displayCurrency}
                                />
                            )}
                            <Space
                                direction="vertical"
                                size={12}
                                style={{
                                    textAlign: "left",
                                    width: "100%",
                                }}
                            >
                                {txCount > 0 ? (
                                    <div>
                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                flexWrap: "wrap",
                                                gap: "8px",
                                                marginBottom: "12px",
                                            }}
                                        >
                                            <h3
                                                style={{
                                                    color: Tokens.textPrimary,
                                                    fontWeight: 500,
                                                    fontSize: "16px",
                                                    margin: 0,
                                                }}
                                            >
                                                Transactions
                                            </h3>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    gap: "6px",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {[
                                                    ["ALL", `All (${txCount})`],
                                                    [
                                                        "BUY",
                                                        `Buy (${buyCount})`,
                                                    ],
                                                    [
                                                        "SELL",
                                                        `Sell (${sellCount})`,
                                                    ],
                                                ].map(([side, label]) => {
                                                    const isActive =
                                                        sideFilter === side;
                                                    return (
                                                        <Tag
                                                            key={side}
                                                            onClick={() =>
                                                                setSideFilter(
                                                                    side
                                                                )
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    isActive
                                                                        ? Tokens.raised
                                                                        : "transparent",
                                                                color: isActive
                                                                    ? Tokens.textPrimary
                                                                    : Tokens.textSecondary,
                                                                border: isActive
                                                                    ? `1px solid ${Tokens.accent}`
                                                                    : `1px solid ${Tokens.hairline}`,
                                                                borderRadius:
                                                                    Tokens.radiusTag,
                                                                cursor: "pointer",
                                                                fontSize:
                                                                    "11px",
                                                                fontWeight:
                                                                    isActive
                                                                        ? 500
                                                                        : 400,
                                                                fontVariantNumeric:
                                                                    "tabular-nums",
                                                                padding:
                                                                    "2px 8px",
                                                                margin: 0,
                                                            }}
                                                        >
                                                            {label}
                                                        </Tag>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        <div
                                            style={{
                                                backgroundColor: Tokens.card,
                                                border: `1px solid ${Tokens.hairline}`,
                                                borderRadius: Tokens.radiusCard,
                                                padding: "12px 16px",
                                                marginBottom: "12px",
                                                display: "grid",
                                                gridTemplateColumns:
                                                    "repeat(auto-fit, minmax(130px, 1fr))",
                                                gap: "12px",
                                            }}
                                        >
                                            <div>
                                                <Text
                                                    style={{
                                                        color: Tokens.textSecondary,
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    Total volume
                                                </Text>
                                                <div
                                                    style={{
                                                        color: Tokens.textPrimary,
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        fontVariantNumeric:
                                                            "tabular-nums",
                                                        marginTop: "2px",
                                                    }}
                                                >
                                                    {currSymbol}
                                                    {getFormattedAmount(
                                                        totalVolume,
                                                        true,
                                                        2
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <Text
                                                    style={{
                                                        color: Tokens.textSecondary,
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    Total bought
                                                </Text>
                                                <div
                                                    style={{
                                                        color: profitColor,
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        fontVariantNumeric:
                                                            "tabular-nums",
                                                        marginTop: "2px",
                                                    }}
                                                >
                                                    {currSymbol}
                                                    {getFormattedAmount(
                                                        totalBought,
                                                        true,
                                                        2
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <Text
                                                    style={{
                                                        color: Tokens.textSecondary,
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    Total sold
                                                </Text>
                                                <div
                                                    style={{
                                                        color: lossColor,
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        fontVariantNumeric:
                                                            "tabular-nums",
                                                        marginTop: "2px",
                                                    }}
                                                >
                                                    {currSymbol}
                                                    {getFormattedAmount(
                                                        totalSold,
                                                        true,
                                                        2
                                                    )}
                                                </div>
                                            </div>
                                            <div>
                                                <Text
                                                    style={{
                                                        color: Tokens.textSecondary,
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    Net flow
                                                </Text>
                                                <div
                                                    style={{
                                                        color:
                                                            netFlow >= 0
                                                                ? profitColor
                                                                : lossColor,
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        fontFamily:
                                                            Tokens.fontMono,
                                                        fontVariantNumeric:
                                                            "tabular-nums",
                                                        marginTop: "2px",
                                                    }}
                                                >
                                                    {netFlow >= 0 ? "+" : "-"}
                                                    {currSymbol}
                                                    {getFormattedAmount(
                                                        Math.abs(netFlow),
                                                        true,
                                                        2
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {filteredTransactions.length > 0 ? (
                                            filteredTransactions.map((item) => (
                                                <List.Item
                                                    key={item.uid}
                                                    style={{
                                                        width: "100%",
                                                        border: "none",
                                                        padding: "0",
                                                    }}
                                                >
                                                    <TransactionListView
                                                        item={item}
                                                        currency={
                                                            displayCurrency
                                                        }
                                                        key={item.uid}
                                                    />
                                                </List.Item>
                                            ))
                                        ) : (
                                            <div
                                                style={{
                                                    backgroundColor:
                                                        Tokens.card,
                                                    border: `1px solid ${Tokens.hairline}`,
                                                    borderRadius:
                                                        Tokens.radiusControl,
                                                    padding: "24px",
                                                    textAlign: "center",
                                                    color: Tokens.textSecondary,
                                                    fontSize: "13px",
                                                }}
                                            >
                                                No {sideFilter.toLowerCase()}{" "}
                                                transactions recorded
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            backgroundColor: Tokens.card,
                                            border: `1px solid ${Tokens.hairline}`,
                                            borderRadius: Tokens.radiusCard,
                                            padding: "32px 24px",
                                            textAlign: "center",
                                            marginTop: "16px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: Tokens.textPrimary,
                                                fontSize: "16px",
                                                fontWeight: 500,
                                                marginBottom: "8px",
                                            }}
                                        >
                                            No transactions recorded yet
                                        </div>
                                        <div
                                            style={{
                                                color: Tokens.textSecondary,
                                                fontSize: "14px",
                                                marginBottom: "16px",
                                            }}
                                        >
                                            Record your first purchase to start
                                            tracking average price and P&amp;L.
                                        </div>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                mpTapGoBack({
                                                    screen: "Asset Manager",
                                                });
                                                setShowBuyDrawer(true);
                                            }}
                                        >
                                            Buy {asset.name}
                                        </Button>
                                    </div>
                                )}
                            </Space>
                        </div>
                    </AppShell>
                    <Transaction
                        title={`Buy ${asset.name}`}
                        visible={showBuyDrawer}
                        onClose={() => setShowBuyDrawer(false)}
                        backCTAAction={() => setShowBuyDrawer(false)}
                        component={<PortfolioOnBuy coinInfo={marketInfo} />}
                    />
                    <Transaction
                        title={`Sell ${asset.name}`}
                        visible={showSellDrawer}
                        onClose={() => setShowSellDrawer(false)}
                        backCTAAction={() => setShowSellDrawer(false)}
                        component={<PortfolioOnSell coinInfo={marketInfo} />}
                    />
                    <MarketView
                        setShowMarket={setShowMarket}
                        showMarket={showMarket}
                        coinInfo={marketInfo}
                        currency={displayCurrency}
                    />
                </div>
            ) : (
                <AppShell
                    activeTab="overview"
                    title={
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <Button
                                type="text"
                                icon={
                                    <ArrowLeftOutlined
                                        style={{
                                            color: Tokens.textSecondary,
                                            fontSize: "16px",
                                        }}
                                    />
                                }
                                onClick={onClose}
                                title="Back to portfolio"
                                style={{
                                    padding: 0,
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            />
                            <Title
                                level={4}
                                style={{
                                    color: Tokens.textPrimary,
                                    margin: 0,
                                    fontWeight: 500,
                                    fontSize: "18px",
                                }}
                            >
                                {`Loading ${asset?.name || ""}`}
                            </Title>
                        </div>
                    }
                    headerStyle={{
                        backgroundColor: Tokens.bg,
                        borderBottom: `1px solid ${Tokens.hairline}`,
                    }}
                    bodyStyle={{
                        backgroundColor: Tokens.bg,
                        padding: "24px 16px",
                    }}
                >
                    <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
                        {[1, 2, 3, 4].map((index) => (
                            <Skeleton
                                loading={loading}
                                title
                                paragraph
                                active
                                avatar
                                key={index}
                            />
                        ))}
                    </div>
                </AppShell>
            )}
        </div>
    );
};

export default AssetManager;
